import { isUndefined } from 'lodash';
import GridRowLabel from 'components/Grid/GridRowLabel/GridRowLabel';
import { ParserParams } from 'models/interfaces/spreadsheet-types/core-types';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { getCashFlowColumns } from './CashFlowColumnConfig';
import { getRowClassByVirtual } from './constants';
import { CashFlowLedgerCellMap, CashFlowLedgerItem, CashFlowLedgerRowData } from './types';

export const CashFlowParser = (params: { rowConfig: CashFlowLedgerItem[] }) => {
  const { rowConfig } = params;
  const colConfig = getCashFlowColumns();
  const alphabet = alphabetGenerator([], colConfig.length);
  const cells: CashFlowLedgerCellMap = {};

  colConfig.forEach((col, colIndex) => {
    const columnLegend = alphabet[colIndex];
    const cellKey = `${columnLegend}1`;
    cells[cellKey] = {
      // valueViewer: (props: any) => <GridRowLabel {...props} />,
      className: ' division-bottom-only text-right',
      value: col.value,
      key: cellKey,
      alias: col.alias,
      columnLegend,
      rowNumber: 1,
      expr: col.value,
    };
  });

  // Create data rows (starting from row 2)
  // rowConfig contains the actual cash flow items (each item is a row)
  rowConfig.forEach((cashFlowItem, rowIndex: number) => {
    const rowNumber = rowIndex + 1; // +2 because row 1 is header

    if (rowIndex === 0) {
      return;
    }

    // colConfig contains the field definitions (each field is a column)
    colConfig.forEach((col, colIndex) => {
      const columnLegend = alphabet[colIndex];
      const cellKey = `${columnLegend}${rowNumber}`;

      // Get the value from the cash flow item using the column alias
      let value = !isUndefined(cashFlowItem[col.alias as keyof CashFlowLedgerItem])
        ? cashFlowItem[col.alias as keyof CashFlowLedgerItem]
        : (col.defaultValue ?? null);

      if (col.alias === 'owner') {
        if (cashFlowItem.owner_fund) {
          value = `fund-${cashFlowItem.owner_fund}`;
        } else if (cashFlowItem.owner_company) {
          value = `company-${cashFlowItem.owner_company}`;
        }
      } else if (col.alias === 'entity') {
        if (cashFlowItem.entity_company) {
          value = `company-${cashFlowItem.entity_company}`;
        }
      } else if (col.alias === 'all_security') {
        value = cashFlowItem.security || null;
      }

      // If this is a virtual row, make it readonly
      const isVirtual = cashFlowItem.is_virtual || false;
      const readOnly = isVirtual || col.readOnly || false;

      if (col.alias === 'amount' && isVirtual) {
        if (cashFlowItem.transaction_type in { 'INVESTMENT': null }) {
          value = -Math.abs(cashFlowItem.amount);
        } else if (cashFlowItem.transaction_type in { 'REALIZED_PROCEEDS': null, 'UNREALIZED_VALUE': null }) {
          value = Math.abs(cashFlowItem.amount);
        }
      }

      // Get the row color class based on is_virtual (green = virtual/readonly, blue = manual/editable)
      const rowClass = getRowClassByVirtual(isVirtual);

      cells[cellKey] = {
        ...col,
        value,
        entityId: cashFlowItem.id,
        key: cellKey,
        alias: col.alias,
        columnLegend,
        rowNumber,
        readOnly,
        is_virtual: isVirtual,
        expr: value?.toString() || '',
        className: rowClass || col.className,
      } as CashFlowLedgerItem;
    });
  });

  return cells;
};
